(() => {
  if (window.QLSCartDrawerUtilitiesV94Loaded) return;
  window.QLSCartDrawerUtilitiesV94Loaded = true;

  const desiredQuantities = new Map();

  let drawerObserver = null;
  let scheduled = false;
  let syncTimer = null;
  let syncInFlight = false;
  let resyncRequested = false;

  function wait(milliseconds) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });
  }

  function getDrawer() {
    return document.querySelector('cart-drawer#cart-drawer, #cart-drawer, cart-drawer');
  }

  function isDrawerOpen(drawer) {
    return Boolean(
      drawer
      && (
        drawer.hasAttribute('open')
        || drawer.getAttribute('aria-hidden') === 'false'
      )
    );
  }

  function cartRoot(path) {
    const root = (
      window.Shopify
      && window.Shopify.routes
      && typeof window.Shopify.routes.root === 'string'
    )
      ? window.Shopify.routes.root
      : '/';

    return root.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }

  function recalculate() {
    document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
  }

  function ensureDeliveryUtility() {
    scheduled = false;

    const drawer = getDrawer();
    if (!drawer) return;

    const row = drawer.querySelector('[data-qls-cart-drawer-utilities]');
    if (!row) return;

    if (row.querySelector('[data-qls-cart-delivery-utility]')) return;

    const template = drawer.querySelector('[data-qls-cart-delivery-utility-template]');
    if (!template?.content) return;

    row.appendChild(template.content.cloneNode(true));
    recalculate();
  }

  function scheduleEnsure() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(ensureDeliveryUtility);
  }

  function stopWatching() {
    if (drawerObserver) {
      drawerObserver.disconnect();
      drawerObserver = null;
    }
  }

  function startWatching() {
    stopWatching();

    const drawer = getDrawer();
    if (!drawer) return;

    scheduleEnsure();

    drawerObserver = new MutationObserver(scheduleEnsure);
    drawerObserver.observe(drawer, {
      childList: true,
      subtree: true
    });
  }

  function normalizeQuantity(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  function isProtectionRow(row) {
    return row?.dataset?.qlsShippingProtection === 'true';
  }

  function recordQuantityInput(input) {
    if (!input) return false;

    const row = input.closest('.cart-item[data-item-key]');
    if (!row || isProtectionRow(row)) return false;

    const itemKey = row.dataset.itemKey;
    const quantity = normalizeQuantity(input.value);

    if (!itemKey || quantity === null) return false;

    desiredQuantities.set(itemKey, quantity);
    return true;
  }

  function collectVisibleDrawerQuantities() {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer
      .querySelectorAll('.cart-item[data-item-key] .qty-input__input')
      .forEach(recordQuantityInput);
  }

  function desiredUpdatesFromCart(cart) {
    const updates = {};

    desiredQuantities.forEach((desiredQuantity, itemKey) => {
      const item = Array.isArray(cart?.items)
        ? cart.items.find((candidate) => candidate.key === itemKey)
        : null;

      if (!item) {
        desiredQuantities.delete(itemKey);
        return;
      }

      if (Number(item.quantity) === Number(desiredQuantity)) {
        desiredQuantities.delete(itemKey);
        return;
      }

      updates[itemKey] = Number(desiredQuantity);
    });

    return updates;
  }

  async function getCart() {
    const response = await fetch(cartRoot('cart.js'), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-QLS-Quantity-Check': 'true'
      },
      cache: 'no-store'
    });

    if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
    return response.json();
  }

  async function applyUpdates(updates) {
    const response = await fetch(cartRoot('cart/update.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-QLS-Quantity-Reconcile': 'true'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
    return response.json();
  }

  async function syncDesiredQuantities(options = {}) {
    collectVisibleDrawerQuantities();

    if (desiredQuantities.size === 0) return true;

    if (syncInFlight) {
      resyncRequested = true;
      return false;
    }

    syncInFlight = true;
    resyncRequested = false;

    try {
      /*
        When the customer is leaving the drawer, allow the theme's native
        debounced change request to finish first. Our final keyed update then
        becomes the last merchandise quantity written to Shopify.
      */
      if (options.beforeNavigation) {
        await wait(520);
        collectVisibleDrawerQuantities();
      }

      let cart = await getCart();
      let updates = desiredUpdatesFromCart(cart);

      if (Object.keys(updates).length > 0) {
        await applyUpdates(updates);
      }

      /*
        Shipping Protection can react immediately after a cart request.
        Verify once after that reaction and reapply only if merchandise was
        changed back.
      */
      await wait(options.beforeNavigation ? 380 : 260);

      cart = await getCart();
      updates = desiredUpdatesFromCart(cart);

      if (Object.keys(updates).length > 0) {
        await applyUpdates(updates);
        await wait(180);

        cart = await getCart();
        desiredUpdatesFromCart(cart);
      }

      return desiredQuantities.size === 0;
    } catch (error) {
      return false;
    } finally {
      syncInFlight = false;

      if (resyncRequested) {
        resyncRequested = false;
        scheduleQuantitySync(220);
      }
    }
  }

  function scheduleQuantitySync(delay = 900) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncDesiredQuantities();
    }, delay);
  }

  async function flushBeforeLeaving() {
    collectVisibleDrawerQuantities();

    if (desiredQuantities.size === 0) return true;

    return syncDesiredQuantities({
      beforeNavigation: true
    });
  }

  /*
    Record the intended value as soon as the quantity field changes. This is
    deliberately separate from the theme's debounced Ajax handler, so clicking
    View cart immediately after + cannot lose the customer's latest value.
  */
  document.addEventListener('change', (event) => {
    const input = event.target.closest?.(
      '#cart-drawer .cart-item[data-item-key] .qty-input__input'
    );

    if (!input || !recordQuantityInput(input)) return;
    scheduleQuantitySync(950);
  }, true);

  /*
    Some quantity components update the input from a button click before their
    change event is dispatched. Capture that visible value on the next frame.
  */
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(
      '#cart-drawer quantity-input button,'
      + '#cart-drawer button[name="plus"],'
      + '#cart-drawer button[name="minus"]'
    );

    if (!button) return;

    window.requestAnimationFrame(() => {
      const input = button
        .closest('quantity-input')
        ?.querySelector('.qty-input__input');

      if (!input || !recordQuantityInput(input)) return;
      scheduleQuantitySync(950);
    });
  }, true);

  document.addEventListener('qls:cart-quantity-committed', (event) => {
    const itemKey = event.detail?.itemKey;
    const quantity = normalizeQuantity(event.detail?.quantity);

    if (!itemKey || quantity === null) return;

    desiredQuantities.set(itemKey, quantity);
    scheduleQuantitySync(700);
  });

  document.addEventListener('qls:shipping-protection:settled', () => {
    if (desiredQuantities.size > 0) {
      scheduleQuantitySync(120);
    }
  });

  document.addEventListener('click', async (event) => {
    const link = event.target.closest?.('[data-qls-cart-page-link]');
    if (!link) return;

    collectVisibleDrawerQuantities();
    if (desiredQuantities.size === 0) return;

    event.preventDefault();

    const destination = link.href;
    await flushBeforeLeaving();
    window.location.assign(destination);
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const submitter = event.submitter;

    if (
      !form
      || form.id !== 'cart-drawer-form'
      || !submitter
      || submitter.name !== 'checkout'
      || form.dataset.qlsQuantityFlushed === 'true'
    ) {
      return;
    }

    collectVisibleDrawerQuantities();
    if (desiredQuantities.size === 0) return;

    event.preventDefault();
    await flushBeforeLeaving();

    form.dataset.qlsQuantityFlushed = 'true';
    form.requestSubmit(submitter);
  }, true);

  document.addEventListener('on:cart-drawer:after-open', startWatching);
  document.addEventListener('on:cart-drawer:after-close', stopWatching);
  document.addEventListener('dispatch:cart-drawer:refresh', scheduleEnsure);
  document.addEventListener('cart:updated', scheduleEnsure);
  document.addEventListener('on:line-item:change', scheduleEnsure);
  document.body?.addEventListener('si.div.check', scheduleEnsure);
  document.body?.addEventListener('si.div.uncheck', scheduleEnsure);

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.('cart-drawer')) {
      const drawer = getDrawer();

      if (isDrawerOpen(drawer)) startWatching();
      else scheduleEnsure();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const drawer = getDrawer();

      if (isDrawerOpen(drawer)) startWatching();
      else scheduleEnsure();
    }, { once: true });
  } else {
    const drawer = getDrawer();

    if (isDrawerOpen(drawer)) startWatching();
    else scheduleEnsure();
  }
})();
